/**
 * /admin/preview-as-ambassador
 *
 * أداة admin-only: يختار الأدمن سفيراً ويرى نسخة طبق الأصل (للقراءة فقط)
 * من ملفّ السفير الكامل كما يراه في `/account/ambassador`:
 * المحفظة + النقرات + المبيعات + Milestones + الكود + شروط المكافآت
 * + سجلّ المدفوعات + الإشعارات + تحليل القنوات.
 *
 * - الحماية: useAdminGate في الواجهة + assertAdmin في server function.
 * - لا تُسجَّل أيّ نقرات (المعاينة لا تستدعي trackClick).
 * - بدون أزرار تفاعليّة (سحب / mark-as-read / تعديل).
 */
import * as React from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Eye,
  X,
  Copy,
  Check,
  Sparkles,
  TrendingUp,
  MousePointerClick,
  ShoppingBag,
  Wallet,
  Instagram,
  Music2,
  MessageCircle,
  Facebook,
  Youtube,
  Send as SendIcon,
  Globe,
  Bell,
  Trophy,
  Banknote,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { optUuid, enumOr } from "@/lib/search-validators";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { useLocale } from "@/contexts/LocaleContext";
import { useAdminGate } from "@/hooks/useAdminGate";
import {
  adminListAmbassadorsForPreview,
  adminGetAmbassadorFullView,
  adminGetAmbassadorAsCustomer,
} from "@/utils/ambassador.functions";
import { RewardConditionsCard } from "@/components/RewardConditionsCard";
import { MyContractCard } from "@/components/MyContractCard";
import { CustomerActivityPanel } from "@/components/admin/CustomerActivityPanel";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

const RANGE_OPTIONS = ["7d", "30d", "90d", "all"] as const;
type RangeOpt = (typeof RANGE_OPTIONS)[number];
const RANGE_TO_DAYS: Record<RangeOpt, number | null> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: null,
};

const searchSchema = (search: Record<string, unknown>): {
  id?: string;
  mode?: "ambassador" | "customer";
  range?: RangeOpt;
} => ({
  id: optUuid(search.id),
  mode: enumOr(search.mode, ["ambassador", "customer"] as const, "ambassador"),
  range: enumOr(search.range, RANGE_OPTIONS, "30d"),
});

export const Route = createFileRoute("/admin/preview-as-ambassador")({
  validateSearch: searchSchema,
  component: PreviewAsAmbassadorPage,
  head: () => ({
    meta: [
      { title: "Preview as Ambassador — Lecmo Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const CHANNEL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: Instagram,
  tiktok: Music2,
  facebook: Facebook,
  youtube: Youtube,
  whatsapp: MessageCircle,
  telegram: SendIcon,
  messenger: MessageCircle,
  snapchat: Sparkles,
  x: Globe,
  copy: Copy,
  direct: Globe,
};

function PreviewAsAmbassadorPage() {
  const { ready, loading } = useAdminGate();
  const { locale, dir, formatPrice, t } = useLocale();
  const navigate = useNavigate();
  const search = useSearch({ from: "/admin/preview-as-ambassador" });
  const ar = locale === "ar";

  const tt = (en: string, arT: string) => (ar ? arT : en);

  const fetchList = useServerFn(adminListAmbassadorsForPreview);
  const fetchView = useServerFn(adminGetAmbassadorFullView);

  const listQuery = useQuery({
    queryKey: ["admin-ambassadors-preview-list"],
    queryFn: () => fetchList(),
    enabled: ready,
  });

  const ambassadors = listQuery.data?.ambassadors ?? [];
  const selectedId = search.id ?? ambassadors[0]?.id ?? null;

  const fetchCustomer = useServerFn(adminGetAmbassadorAsCustomer);

  const mode = (search.mode ?? "ambassador") as "ambassador" | "customer";
  const range = (search.range ?? "30d") as RangeOpt;
  const rangeDays = RANGE_TO_DAYS[range];

  const viewQuery = useQuery({
    queryKey: ["admin-ambassador-full-view", selectedId, range],
    queryFn: () => fetchView({ data: { ambassadorId: selectedId!, rangeDays } }),
    enabled: ready && !!selectedId && mode === "ambassador",
  });

  const customerQuery = useQuery({
    queryKey: ["admin-ambassador-as-customer", selectedId, range],
    queryFn: () => fetchCustomer({ data: { ambassadorId: selectedId!, rangeDays } }),
    enabled: ready && !!selectedId && mode === "customer",
  });

  const [copiedCode, setCopiedCode] = React.useState(false);
  const [copiedLink, setCopiedLink] = React.useState(false);

  const data = viewQuery.data;
  const ambassador = data?.ambassador;
  const stats = data?.stats;
  const balances = data?.balances ?? [];
  const rewardRules = data?.rewardRules ?? [];
  const channels = data?.channels ?? [];
  const topProducts = data?.topProducts ?? [];
  const payouts = data?.payouts ?? [];
  const notifications = data?.notifications ?? [];
  const milestones = data?.milestones ?? [];
  const walletAccount = data?.walletAccount ?? null;
  const recentClicks = data?.recentClicks ?? [];
  const recentOrders = data?.recentOrders ?? [];
  const recentWalletTxns = data?.recentWalletTxns ?? [];

  const code = ambassador?.code ?? null;
  const displayName = ambassador?.display_name ?? "";
  const currency = balances[0]?.currency || "AED";

  const shareUrl = React.useMemo(() => {
    if (!code) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : PUBLIC_SITE_ORIGIN;
    return `${origin}/?ref=${code}`;
  }, [code]);

  const onCopyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(true);
      toast.success(tt("Copied", "تم النسخ"));
      setTimeout(() => setCopiedCode(false), 2000);
    } catch {/* ignore */}
  };
  const onCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      toast.success(tt("Copied", "تم النسخ"));
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {/* ignore */}
  };

  function selectAmb(id: string) {
    navigate({
      to: "/admin/preview-as-ambassador",
      search: { id, mode, range },
      replace: true,
    });
  }
  function setMode(next: "ambassador" | "customer") {
    navigate({
      to: "/admin/preview-as-ambassador",
      search: { id: selectedId ?? undefined, mode: next, range },
      replace: true,
    });
  }
  function setRange(next: RangeOpt) {
    navigate({
      to: "/admin/preview-as-ambassador",
      search: { id: selectedId ?? undefined, mode, range: next },
      replace: true,
    });
  }

  if (loading) {
    return (
      <SiteShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <p className="text-sm text-muted-foreground">…</p>
        </div>
      </SiteShell>
    );
  }
  if (!ready) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-3xl px-4 py-12" dir={dir}>
          <p className="text-sm text-muted-foreground">{tt("Admin only.", "للأدمن فقط.")}</p>
        </div>
      </SiteShell>
    );
  }

  const maxRevenue = channels.reduce((m, c) => Math.max(m, c.revenue || 0), 0);
  const maxClicks = channels.reduce((m, c) => Math.max(m, c.clicks || 0), 0);
  const topChannel = channels[0];

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 pt-4">
        <AdminNav />
      </div>
      {/* Sticky admin bar */}
      <div
        className="sticky top-[var(--header-height,64px)] z-30 border-y border-primary/30 bg-primary/5 backdrop-blur"
        dir={dir}
      >
        <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3 px-4 py-3">
          <span className="inline-flex items-center gap-1.5 bg-primary px-2.5 py-1 text-[10px] uppercase tracking-[var(--tracking-wider)] text-primary-foreground">
            <Eye className="h-3 w-3" />
            {tt("Preview Mode", "وضع المعاينة")}
          </span>
          <span className="hidden text-xs text-muted-foreground sm:inline">
            {tt("Read-only · no clicks recorded.", "قراءة فقط · لا تُسجَّل نقرات.")}
          </span>

          <div className="ms-auto flex flex-wrap items-center gap-2">
            {/* Mode toggle */}
            <div className="inline-flex border border-border bg-background text-xs">
              <button
                onClick={() => setMode("ambassador")}
                className={`px-2.5 py-1 ${mode === "ambassador" ? "bg-primary text-primary-foreground" : "text-foreground hover:text-primary"}`}
              >
                {tt("Ambassador", "سفير")}
              </button>
              <button
                onClick={() => setMode("customer")}
                className={`px-2.5 py-1 ${mode === "customer" ? "bg-primary text-primary-foreground" : "text-foreground hover:text-primary"}`}
              >
                {tt("Customer", "عميل")}
              </button>
            </div>
            {/* Range */}
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as RangeOpt)}
              className="border border-border bg-background px-2 py-1 text-xs"
              aria-label={tt("Date range", "النطاق الزمنيّ")}
            >
              <option value="7d">{tt("Last 7 days", "آخر ٧ أيّام")}</option>
              <option value="30d">{tt("Last 30 days", "آخر ٣٠ يوماً")}</option>
              <option value="90d">{tt("Last 90 days", "آخر ٩٠ يوماً")}</option>
              <option value="all">{tt("All time", "كلّ الوقت")}</option>
            </select>
            {/* Ambassador picker */}
            <select
              id="amb-pick"
              value={selectedId ?? ""}
              onChange={(e) => selectAmb(e.target.value)}
              className="border border-border bg-background px-2 py-1 text-xs"
            >
              {ambassadors.length === 0 && (
                <option value="">{tt("Loading…", "تحميل…")}</option>
              )}
              {ambassadors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name || a.code} · {a.code}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined" && window.history.length > 1) {
                  window.history.back();
                } else {
                  navigate({ to: "/admin/ambassadors" });
                }
              }}
              className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs text-foreground hover:border-primary hover:text-primary"
            >
              <ArrowLeft className={`h-3 w-3 ${dir === "rtl" ? "rotate-180" : ""}`} />
              {tt("Back", "رجوع")}
            </button>
            {selectedId && (
              <Link
                to="/admin/ambassador-monitor"
                search={{ id: selectedId, range }}
                className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs text-foreground hover:border-primary hover:text-primary"
              >
                <TrendingUp className="h-3 w-3" />
                {tt("Monitor & actions", "المراقبة والقرارات")}
              </Link>
            )}

          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8" dir={dir}>
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined" && window.history.length > 1) {
              window.history.back();
            } else {
              navigate({ to: "/admin/ambassadors" });
            }
          }}
          className="mb-6 inline-flex items-center gap-2 t-caption uppercase tracking-[var(--tracking-wider)] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className={`h-3.5 w-3.5 ${dir === "rtl" ? "rotate-180" : ""}`} />
          {tt("Back to previous page", "العودة للصفحة السابقة")}
        </button>

        <div className="mb-8 flex items-start gap-3">
          <Sparkles className="mt-1 h-6 w-6 shrink-0 text-primary" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="t-h2 text-foreground">
                {displayName
                  ? tt(`Viewing as ${displayName}`, `معاينة كـ ${displayName}`)
                  : tt("Preview as Ambassador", "معاينة كسفير")}
              </h1>
              {ambassador?.relationship_type === "signature_partner" && (
                <span className="rounded border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                  {t("ambassador.relationship.signature_partner")}
                </span>
              )}
              {ambassador?.relationship_type === "vip_guest" && (
                <span className="rounded border border-purple-500/40 bg-purple-500/15 px-2 py-0.5 text-[10px] font-medium text-purple-700 dark:text-purple-400">
                  {t("ambassador.relationship.vip_guest")}
                </span>
              )}
            </div>
            <p className="mt-1 t-body-sm text-muted-foreground">
              {tt(
                "Same dashboard the ambassador sees — read-only.",
                "نفس الداشبورد الذي يراه السفير — للقراءة فقط.",
              )}
            </p>
          </div>
        </div>

        {mode === "customer" ? (
          customerQuery.isLoading ? (
            <div className="space-y-4">
              <div className="h-32 animate-pulse bg-secondary" />
              <div className="h-24 animate-pulse bg-secondary" />
            </div>
          ) : customerQuery.data ? (
            <CustomerActivityPanel data={customerQuery.data} ar={ar} formatPrice={formatPrice} />
          ) : (
            <div className="border border-border bg-secondary/30 p-6 text-center text-sm text-muted-foreground">
              {tt("No data.", "لا بيانات.")}
            </div>
          )
        ) : viewQuery.isLoading ? (
          <div className="space-y-4">
            <div className="h-32 animate-pulse bg-secondary" />
            <div className="h-24 animate-pulse bg-secondary" />
            <div className="h-48 animate-pulse bg-secondary" />
          </div>
        ) : !ambassador ? (
          <div className="border border-border bg-secondary/30 p-6 text-center text-sm text-muted-foreground">
            {tt("No ambassador selected.", "لم يُختَر سفير.")}
          </div>
        ) : (
          <div className="space-y-6">
            {/* بطاقة الكود */}
            <div className="border border-primary/40 bg-primary/5 p-5">
              <p className="t-eyebrow uppercase text-muted-foreground">
                {t("account.ambassador.code_label")}
              </p>
              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="t-h1 tracking-[0.25em] text-primary">{ambassador.code}</span>
                <button
                  onClick={onCopyCode}
                  className="flex shrink-0 items-center gap-2 border border-primary px-3 py-2 t-caption uppercase tracking-[var(--tracking-wider)] text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                >
                  {copiedCode ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copiedCode ? tt("Copied", "تم النسخ") : tt("Copy", "نسخ")}
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 border-t border-primary/20 pt-3">
                <code className="truncate text-xs text-muted-foreground" dir="ltr">{shareUrl}</code>
                <button onClick={onCopyLink} className="shrink-0 text-xs text-primary hover:underline">
                  {copiedLink ? tt("Copied", "تم النسخ") : tt("Copy link", "نسخ الرابط")}
                </button>
              </div>
            </div>

            {/* عقد سفير العلامة */}
            {ambassador.relationship_type === "signature_partner" && (
              <MyContractCard
                startsAt={ambassador.contract_starts_at ?? null}
                endsAt={ambassador.contract_ends_at ?? null}
                monthlyFee={ambassador.monthly_fee_aed ?? null}
                currency={ambassador.contract_currency ?? "AED"}
                deliverables={ambassador.deliverables_note ?? null}
              />
            )}

            {/* بطاقة الرصيد */}
            <div className="border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2">
                <Wallet className="h-4 w-4 text-primary" />
                <h2 className="t-h3 text-foreground">{t("account.ambassador.balance.title")}</h2>
              </div>
              {balances.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("account.ambassador.balance.empty")}</p>
              ) : (
                <div className="space-y-3">
                  {balances.map((b) => (
                    <div key={b.currency} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{t("account.ambassador.balance.available")}</span>
                        <span className="t-h3 text-primary">{formatPrice(Number(b.available_credit), b.currency)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{t("account.ambassador.balance.pending")}</span>
                        <span className="text-foreground">{formatPrice(Number(b.pending_credit), b.currency)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{t("account.ambassador.balance.paid")}</span>
                        <span className="text-foreground">{formatPrice(Number(b.paid_out), b.currency)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* شروط الأرباح */}
            <RewardConditionsCard
              tier={ambassador.tier}
              rules={rewardRules}
              locale={locale}
              t={t}
              formatPrice={formatPrice}
              currency={currency}
            />

            {/* محفظة السفير — قراءة فقط */}
            <div className="border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2">
                <Banknote className="h-4 w-4 text-primary" />
                <h2 className="t-h3 text-foreground">{tt("Bank account", "الحساب البنكيّ")}</h2>
              </div>
              {!walletAccount ? (
                <p className="text-sm text-muted-foreground">
                  {tt("No bank account on file.", "لا يوجد حساب بنكيّ مسجَّل.")}
                </p>
              ) : (
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between gap-3"><span className="text-muted-foreground">{tt("Holder", "الاسم")}</span><span className="text-foreground">{walletAccount.account_holder_name}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-muted-foreground">{tt("Bank", "البنك")}</span><span className="text-foreground">{walletAccount.bank_name}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-muted-foreground">{tt("Country", "الدولة")}</span><span className="text-foreground" dir="ltr">{walletAccount.country}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-muted-foreground">IBAN</span><span className="text-foreground" dir="ltr">•••• {walletAccount.iban_last4}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-muted-foreground">{tt("Verified", "موثَّق")}</span><span className="text-foreground">{walletAccount.is_verified ? "✓" : "—"}</span></div>
                </div>
              )}
            </div>

            {/* سجلّ المدفوعات */}
            <div className="border border-border bg-card p-5">
              <h2 className="mb-3 t-h3 text-foreground">{tt("Payout history", "سجلّ المدفوعات")}</h2>
              {payouts.length === 0 ? (
                <p className="text-sm text-muted-foreground">{tt("No payouts yet.", "لا توجد مدفوعات بعد.")}</p>
              ) : (
                <div className="divide-y divide-border">
                  {payouts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="text-foreground" dir="ltr">{formatPrice(Number(p.amount), p.currency)}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {new Date(p.requested_at).toLocaleDateString(ar ? "ar" : "en")}
                        </p>
                      </div>
                      <span className={`shrink-0 border px-2 py-0.5 text-[10px] uppercase tracking-[var(--tracking-wider)] ${
                        p.status === "paid" ? "border-green-500/40 text-green-700 dark:text-green-400"
                        : p.status === "rejected" ? "border-destructive/40 text-destructive"
                        : "border-border text-muted-foreground"
                      }`}>{p.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* الإشعارات */}
            <div className="border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2">
                <Bell className="h-4 w-4 text-primary" />
                <h2 className="t-h3 text-foreground">{tt("Notifications", "الإشعارات")}</h2>
              </div>
              {notifications.length === 0 ? (
                <p className="text-sm text-muted-foreground">{tt("No notifications.", "لا توجد إشعارات.")}</p>
              ) : (
                <div className="divide-y divide-border">
                  {notifications.map((n) => {
                    const title = ar ? n.title_ar : n.title_en;
                    const body = ar ? n.body_ar : n.body_en;
                    return (
                      <div key={n.id} className={`py-2.5 ${!n.read_at ? "" : "opacity-60"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-foreground">{title || n.notification_type}</p>
                          {!n.read_at && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                        </div>
                        {body && <p className="mt-0.5 text-xs text-muted-foreground">{body}</p>}
                        {n.amount && (
                          <p className="mt-0.5 text-xs text-primary" dir="ltr">
                            {formatPrice(Number(n.amount), n.currency || "AED")}
                          </p>
                        )}
                        <p className="mt-0.5 text-[10px] text-muted-foreground/70">
                          {new Date(n.created_at).toLocaleString(ar ? "ar" : "en")}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Milestones */}
            <div className="border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-primary" />
                <h2 className="t-h3 text-foreground">{tt("Milestones", "الإنجازات")}</h2>
              </div>
              {milestones.length === 0 ? (
                <p className="text-sm text-muted-foreground">{tt("No active milestones.", "لا توجد إنجازات نشطة.")}</p>
              ) : (
                <div className="space-y-3">
                  {milestones.map((m) => {
                    const title = ar ? m.title_ar : m.title_en;
                    const desc = ar ? m.description_ar : m.description_en;
                    const pct = Math.min(100, Math.round((m.progress / Math.max(1, m.required_count)) * 100));
                    return (
                      <div key={m.id} className={`border border-border bg-background p-3 ${!m.eligible ? "opacity-50" : ""}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground">{title}</p>
                          <span className="text-xs text-muted-foreground">{m.progress}/{m.required_count}</span>
                        </div>
                        {desc && <p className="mt-0.5 text-[11px] text-muted-foreground">{desc}</p>}
                        <div className="mt-2 h-1.5 w-full overflow-hidden bg-secondary">
                          <div className={`h-full transition-all ${m.completed ? "bg-green-600" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                        </div>
                        {m.completed && (
                          <p className="mt-1 text-[11px] text-green-700 dark:text-green-400">
                            ✓ {tt("Completed", "مُكتمَل")}
                            {m.rewardAmount && ` · ${formatPrice(Number(m.rewardAmount), m.rewardCurrency || "AED")}`}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* إحصائيّات الشهر */}
            <div className="border border-border bg-card p-5">
              <h2 className="mb-3 t-h3 text-foreground">{t("account.ambassador.stats.title")}</h2>
              <div className="grid grid-cols-3 gap-2">
                <div className="flex flex-col items-center gap-1 border border-border bg-background p-3 text-center">
                  <MousePointerClick className="h-4 w-4 text-primary" />
                  <span className="t-h3 text-foreground">{stats?.clicks_30d ?? 0}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">{t("account.ambassador.stats.clicks")}</span>
                </div>
                <div className="flex flex-col items-center gap-1 border border-border bg-background p-3 text-center">
                  <ShoppingBag className="h-4 w-4 text-primary" />
                  <span className="t-h3 text-foreground">{stats?.sales_30d ?? 0}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">{t("account.ambassador.stats.sales")}</span>
                </div>
                <div className="flex flex-col items-center gap-1 border border-border bg-background p-3 text-center">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <span className="t-h3 text-foreground" dir="ltr">{formatPrice(stats?.revenue_30d ?? 0, currency)}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">{t("account.ambassador.channels.col_earnings")}</span>
                </div>
              </div>
            </div>

            {/* تحليل القنوات */}
            <div className="border border-border bg-card p-5">
              <div className="mb-1 flex items-center gap-2 text-center text-sm">
                <TrendingUp className="h-4 w-4 text-primary" />
                <h2 className="t-h3 text-foreground">{t("account.ambassador.channels.title")}</h2>
              </div>
              <p className="mb-4 text-xs text-muted-foreground">{t("account.ambassador.channels.subtitle")}</p>

              {channels.length === 0 ? (
                <div className="border border-dashed border-border bg-secondary/30 p-6 text-center text-sm text-muted-foreground">
                  {t("account.ambassador.channels.empty")}
                </div>
              ) : (
                <div className="space-y-3">
                  {channels.map((c) => {
                    const Icon = CHANNEL_ICONS[c.source] ?? Globe;
                    const barBasis = maxRevenue > 0 ? c.revenue : c.clicks;
                    const barMax = maxRevenue > 0 ? maxRevenue : maxClicks;
                    const pct = barMax > 0 ? Math.max(4, Math.round((barBasis / barMax) * 100)) : 0;
                    return (
                      <div key={c.source} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="h-4 w-4 shrink-0 text-primary" />
                            <span className="truncate text-sm font-medium text-foreground capitalize">{c.source}</span>
                          </div>
                          <span className="shrink-0 text-sm text-foreground" dir="ltr">
                            {c.revenue > 0 ? formatPrice(c.revenue, currency) : `${c.clicks} clicks`}
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden bg-secondary">
                          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                          <span>{c.clicks} {t("account.ambassador.channels.col_clicks").toLowerCase()}</span>
                          <span>•</span>
                          <span>{c.sales} {t("account.ambassador.channels.col_sales").toLowerCase()}</span>
                        </div>
                      </div>
                    );
                  })}
                  {topChannel && topChannel.revenue > 0 && (
                    <p className="mt-4 border-t border-border pt-3 text-xs text-primary capitalize">
                      💡 {t("account.ambassador.channels.tip").replace("{channel}", topChannel.source)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* أعلى المنتجات */}
            {topProducts.length > 0 && (
              <div className="border border-border bg-card p-5">
                <h2 className="mb-3 t-h3 text-foreground">{t("account.ambassador.products.title")}</h2>
                <div className="space-y-2">
                  {topProducts.map((p, idx) => (
                    <div key={p.handle} className="flex items-center gap-3 border border-border bg-background p-3">
                      <span className="t-h3 text-muted-foreground">{idx + 1}</span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{p.title}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{p.clicks} · {p.sales}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* تحقيق تفصيليّ — جداول خام (collapsible) */}
            <details className="group border border-border bg-card">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-4 hover:bg-secondary/30">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <h2 className="t-h3 text-foreground">
                    {tt("Detailed investigation", "تحقيق تفصيليّ")}
                  </h2>
                </div>
                <span className="t-caption uppercase tracking-[var(--tracking-wider)] text-muted-foreground transition-transform group-open:rotate-180">
                  ▾
                </span>
              </summary>

              <div className="space-y-6 border-t border-border p-4">
                {/* آخر النقرات */}
                <div>
                  <h3 className="mb-2 t-eyebrow uppercase text-muted-foreground">
                    {tt(`Last ${recentClicks.length} clicks`, `آخر ${recentClicks.length} نقرة`)}
                  </h3>
                  {recentClicks.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{tt("No clicks.", "لا توجد نقرات.")}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-xs">
                        <thead className="border-b border-border text-muted-foreground">
                          <tr>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Time", "الوقت")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Country", "البلد")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Device", "الجهاز")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Source", "المصدر")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Landing", "الصفحة")}</th>
                            <th className="px-2 py-1.5 text-end font-medium">{tt("Reward", "المكافأة")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {recentClicks.map((c) => {
                            const meta = (c.metadata ?? {}) as Record<string, unknown>;
                            return (
                              <tr key={c.id} className="text-foreground">
                                <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground" dir="ltr">
                                  {new Date(c.created_at).toLocaleString(ar ? "ar" : "en", { dateStyle: "short", timeStyle: "short" })}
                                </td>
                                <td className="px-2 py-1.5" dir="ltr">{c.country || "—"}</td>
                                <td className="px-2 py-1.5 capitalize">{c.device || "—"}</td>
                                <td className="px-2 py-1.5 capitalize">{String(meta.source ?? "direct")}</td>
                                <td className="px-2 py-1.5 max-w-[200px] truncate text-muted-foreground" dir="ltr" title={c.landing_path || ""}>
                                  {c.landing_path || "—"}
                                </td>
                                <td className="px-2 py-1.5 text-end" dir="ltr">
                                  {Number(c.computed_reward) > 0 ? formatPrice(Number(c.computed_reward), c.currency || "AED") : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* آخر الطلبات المنسوبة */}
                <div>
                  <h3 className="mb-2 t-eyebrow uppercase text-muted-foreground">
                    {tt(`Last ${recentOrders.length} attributed orders`, `آخر ${recentOrders.length} طلب منسوب`)}
                  </h3>
                  {recentOrders.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{tt("No orders attributed yet.", "لا توجد طلبات منسوبة بعد.")}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[500px] text-xs">
                        <thead className="border-b border-border text-muted-foreground">
                          <tr>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Time", "الوقت")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Order", "الطلب")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Country", "البلد")}</th>
                            <th className="px-2 py-1.5 text-end font-medium">{tt("Total", "الإجماليّ")}</th>
                            <th className="px-2 py-1.5 text-end font-medium">{tt("Commission", "العمولة")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {recentOrders.map((o) => (
                            <tr key={o.id} className="text-foreground">
                              <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground" dir="ltr">
                                {new Date(o.created_at).toLocaleDateString(ar ? "ar" : "en")}
                              </td>
                              <td className="px-2 py-1.5 font-mono text-[11px]" dir="ltr">{o.order_id || "—"}</td>
                              <td className="px-2 py-1.5" dir="ltr">{o.country || "—"}</td>
                              <td className="px-2 py-1.5 text-end" dir="ltr">
                                {o.order_total ? formatPrice(Number(o.order_total), o.currency || "AED") : "—"}
                              </td>
                              <td className="px-2 py-1.5 text-end text-primary" dir="ltr">
                                {Number(o.computed_reward) > 0 ? formatPrice(Number(o.computed_reward), o.currency || "AED") : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* آخر حركات المحفظة */}
                <div>
                  <h3 className="mb-2 t-eyebrow uppercase text-muted-foreground">
                    {tt(`Last ${recentWalletTxns.length} wallet transactions`, `آخر ${recentWalletTxns.length} حركة محفظة`)}
                  </h3>
                  {recentWalletTxns.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{tt("No transactions.", "لا توجد حركات.")}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-xs">
                        <thead className="border-b border-border text-muted-foreground">
                          <tr>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Time", "الوقت")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Type", "النوع")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("State", "الحالة")}</th>
                            <th className="px-2 py-1.5 text-end font-medium">{tt("Amount", "المبلغ")}</th>
                            <th className="px-2 py-1.5 text-start font-medium">{tt("Note", "ملاحظة")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {recentWalletTxns.map((w) => (
                            <tr key={w.id} className="text-foreground">
                              <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground" dir="ltr">
                                {new Date(w.created_at).toLocaleDateString(ar ? "ar" : "en")}
                              </td>
                              <td className="px-2 py-1.5 capitalize">{w.txn_type}</td>
                              <td className="px-2 py-1.5">
                                <span className={`border px-1.5 py-0.5 text-[10px] uppercase ${
                                  w.balance_state === "available" ? "border-green-500/40 text-green-700 dark:text-green-400"
                                  : w.balance_state === "pending" ? "border-amber-500/40 text-amber-700 dark:text-amber-400"
                                  : "border-border text-muted-foreground"
                                }`}>{w.balance_state}</span>
                              </td>
                              <td className="px-2 py-1.5 text-end" dir="ltr">
                                {formatPrice(Number(w.amount), w.currency || "AED")}
                              </td>
                              <td className="px-2 py-1.5 max-w-[200px] truncate text-muted-foreground" title={w.note || ""}>
                                {w.note || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </details>

            {/* مكتبة الميديا — رابط للوحة المنفصلة */}
            <div className="border border-dashed border-border bg-secondary/20 p-4 text-center">
              <ImageIcon className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                {tt(
                  "Media library is shared across all ambassadors — manage it from the admin media panel.",
                  "مكتبة الميديا مشتركة بين السفراء — تُدار من لوحة media للأدمن.",
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </SiteShell>
  );
}
